import { Suspense, useEffect, useMemo, useState } from 'react';
import { NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { ChevronsLeft, ChevronsRight, CircleUserRound, LogOut, Menu, Moon, Sun, UserRound } from 'lucide-react';
import { APP_ROUTES, NAV_GROUPS, PLATFORM_ONLY_GROUPS, type AppRoute } from '@/app/registry';
import { useAuth, useMe, usePermissions } from '@/auth/useAuth';
import { cn } from '@/lib/cn';
import { hasAnyPerm, isPlatformOnly } from '@/lib/permissions';
import { storage } from '@/lib/storage';
import { applyTheme, getTheme, type Theme } from '@/lib/theme';
import { Avatar } from '@/components/ui/Avatar';
import { Drawer } from '@/components/ui/Drawer';
import { SkeletonBlock } from '@/components/ui/Feedback';
import { MenuItem, Popover } from '@/components/ui/Popover';
import { NotificationsBell } from './NotificationsBell';
import { titleBadge } from '@/lib/titleBadge';

function useNavGroups() {
  const perms = usePermissions();
  return useMemo(
    () =>
      NAV_GROUPS.filter((g) => !isPlatformOnly(perms) || PLATFORM_ONLY_GROUPS.includes(g.id))
        .map((g) => ({
          ...g,
          items: APP_ROUTES.filter(
            (r): r is AppRoute & { nav: NonNullable<AppRoute['nav']> } => r.nav?.group === g.id && hasAnyPerm(perms, r.perms),
          ),
        }))
        .filter((g) => g.items.length > 0),
    [perms],
  );
}

function SidebarNav({ collapsed, onNavigate }: { collapsed: boolean; onNavigate?: () => void }) {
  const groups = useNavGroups();
  return (
    <nav aria-label="Điều hướng chính" className="scrollbar-thin flex-1 overflow-y-auto px-2 py-3">
      {groups.map((g) => (
        <div key={g.id} className="mb-4">
          {!collapsed && <p className="mb-1 px-2.5 text-2xs font-semibold tracking-wide text-subtle uppercase">{g.label}</p>}
          {collapsed && <div className="mx-2.5 mb-1 border-t border-line" aria-hidden />}
          <ul className="flex flex-col gap-0.5">
            {g.items.map((r) => {
              const Icon = r.nav.icon;
              return (
                <li key={r.path}>
                  <NavLink
                    to={r.nav.to ?? `/${r.path}`}
                    end={r.nav.end}
                    onClick={onNavigate}
                    title={collapsed ? r.nav.label : undefined}
                    className={({ isActive }) =>
                      cn(
                        'relative flex min-h-8 items-center gap-2.5 rounded-md px-2.5 py-1.5 text-[13px] transition-colors duration-150 max-lg:min-h-11',
                        collapsed && 'justify-center px-0',
                        isActive
                          ? 'bg-accent-soft font-medium text-accent before:absolute before:inset-y-1.5 before:-left-2 before:w-[3px] before:rounded-r before:bg-accent'
                          : 'text-muted hover:bg-surface-2 hover:text-ink',
                      )
                    }
                  >
                    <Icon className="size-4 shrink-0" aria-hidden />
                    <span className={cn('min-w-0 truncate', collapsed && 'sr-only')}>{r.nav.label}</span>
                  </NavLink>
                </li>
              );
            })}
          </ul>
        </div>
      ))}
    </nav>
  );
}

function Brand({ collapsed }: { collapsed?: boolean }) {
  return (
    <div className={cn('flex h-14 shrink-0 items-center gap-2.5 border-b border-line px-4', collapsed && 'justify-center px-0')}>
      <span className="flex size-7 items-center justify-center rounded-md bg-accent text-[13px] font-bold text-accent-ink" aria-hidden>
        NB
      </span>
      {!collapsed && (
        <span className="min-w-0 leading-tight">
          <span className="block truncate text-[13px] font-semibold">Nguyên Bình CRM</span>
          <span className="block truncate text-2xs text-subtle">Đào tạo & dịch vụ</span>
        </span>
      )}
    </div>
  );
}

/** Tenant line under the user's name: the SYSTEM tenant is the operator, shown as the console, not as a company. */
function tenantLabel(me: { tenantKind?: string; tenantName?: string } | undefined) {
  if (!me) return undefined;
  return me.tenantKind === 'SYSTEM' ? 'Quản trị nền tảng' : me.tenantName;
}

function UserMenu() {
  const { data: me } = useMe();
  const tenant = tenantLabel(me);
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [theme, setTheme] = useState<Theme>(getTheme);
  return (
    <Popover
      role="menu"
      label="Tài khoản"
      trigger={(p) => (
        <button
          type="button"
          {...p}
          className="flex min-h-10 items-center gap-2 rounded-md py-1 pr-1 pl-1 transition-colors hover:bg-surface-2 max-sm:min-h-11 md:pr-2"
          aria-label="Tài khoản"
        >
          <Avatar name={me?.displayName} src={me?.avatarUrl} id={me?.id} size="md" fallback={<UserRound className="size-4" />} />
          <span className="hidden min-w-0 text-left leading-tight md:block">
            <span className="block max-w-[200px] truncate text-[13px] font-medium">{me?.displayName}</span>
            <span className="block max-w-[200px] truncate text-2xs text-subtle" title={tenant}>
              {tenant}
            </span>
          </span>
        </button>
      )}
    >
      {(close) => (
        <div className="py-1">
          <div className="flex max-w-[300px] items-start gap-3 border-b border-line px-3 pt-1.5 pb-2.5">
            <Avatar name={me?.displayName} src={me?.avatarUrl} id={me?.id} size="lg" />
            <div className="min-w-0">
              <p className="text-[13px] font-medium">{me?.displayName}</p>
              <p className="break-anywhere text-xs text-subtle">{me?.email}</p>
              {tenant && <p className="text-xs text-subtle">{tenant}</p>}
              {me?.jobTitle && <p className="text-xs text-subtle">{me.jobTitle}</p>}
            </div>
          </div>
          <MenuItem
            icon={<CircleUserRound className="size-4" aria-hidden />}
            onClick={() => {
              close();
              void navigate('/account');
            }}
          >
            Tài khoản của tôi
          </MenuItem>
          <MenuItem
            icon={theme === 'dark' ? <Sun className="size-4" aria-hidden /> : <Moon className="size-4" aria-hidden />}
            onClick={() => {
              const next = theme === 'dark' ? 'light' : 'dark';
              applyTheme(next);
              setTheme(next);
            }}
          >
            {theme === 'dark' ? 'Giao diện sáng' : 'Giao diện tối'}
          </MenuItem>
          <MenuItem
            danger
            icon={<LogOut className="size-4" aria-hidden />}
            onClick={() => {
              close();
              void logout();
            }}
          >
            Đăng xuất
          </MenuItem>
        </div>
      )}
    </Popover>
  );
}

/** Authenticated layout: grouped sidebar (collapsible; drawer on phones), topbar (bell, user menu), routed content. */
export function AppShell() {
  const [collapsed, setCollapsed] = useState(() => storage.get('ui.sidebarCollapsed') === '1');
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    applyTheme(getTheme());
  }, []);

  useEffect(() => {
    const route = APP_ROUTES.find((r) => matchesPath(r.path, location.pathname));
    titleBadge.setBase(route ? `${route.title} · CRM Nguyên Bình` : 'CRM Nguyên Bình');
  }, [location.pathname]);

  const toggle = () => {
    setCollapsed((c) => {
      storage.set('ui.sidebarCollapsed', c ? '0' : '1');
      return !c;
    });
  };

  return (
    <div className="flex min-h-screen bg-bg">
      <a
        href="#main"
        className="sr-only z-50 rounded bg-accent text-accent-ink focus:not-sr-only focus:fixed focus:top-2 focus:left-2 focus:px-3 focus:py-2"
      >
        Bỏ qua điều hướng
      </a>
      <aside
        className={cn(
          'sticky top-0 hidden h-screen shrink-0 flex-col border-r border-line bg-surface transition-[width] lg:flex',
          collapsed ? 'w-[60px]' : 'w-[248px]',
        )}
      >
        <Brand collapsed={collapsed} />
        <SidebarNav collapsed={collapsed} />
        <button
          type="button"
          onClick={toggle}
          className="flex h-10 shrink-0 items-center justify-center gap-2 border-t border-line text-xs text-subtle transition-colors hover:bg-surface-2 hover:text-ink"
          aria-label={collapsed ? 'Mở rộng thanh bên' : 'Thu gọn thanh bên'}
          title={collapsed ? 'Mở rộng thanh bên' : undefined}
        >
          {collapsed ? <ChevronsRight className="size-4" /> : <ChevronsLeft className="size-4" />}
          {!collapsed && 'Thu gọn'}
        </button>
      </aside>

      <Drawer
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        title={
          <span className="flex items-center gap-2.5">
            <span
              className="flex size-7 shrink-0 items-center justify-center rounded-md bg-accent text-[13px] font-bold text-accent-ink"
              aria-hidden
            >
              NB
            </span>
            Nguyên Bình CRM
          </span>
        }
        side="left"
        width="max-w-[300px]"
        bodyClassName="p-0 sm:p-0"
      >
        <div className="flex h-full flex-col">
          <SidebarNav collapsed={false} onNavigate={() => setMobileOpen(false)} />
        </div>
      </Drawer>

      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-14 shrink-0 items-center gap-2 border-b border-line bg-surface px-2 sm:px-3 md:px-(--space-gutter)">
          <button
            type="button"
            className="inline-flex size-11 items-center justify-center rounded-md text-muted transition-colors hover:bg-surface-2 hover:text-ink sm:size-9 lg:hidden"
            onClick={() => setMobileOpen(true)}
            aria-label="Mở menu"
          >
            <Menu className="size-5" />
          </button>
          <span className="flex min-w-0 items-center gap-2 lg:hidden">
            <span
              className="flex size-6 shrink-0 items-center justify-center rounded bg-accent text-2xs font-bold text-accent-ink"
              aria-hidden
            >
              NB
            </span>
            <span className="truncate text-[13px] font-semibold">Nguyên Bình CRM</span>
          </span>
          <div className="ml-auto flex items-center gap-1">
            <NotificationsBell />
            <UserMenu />
          </div>
        </header>
        <main id="main" tabIndex={-1} className="mx-auto w-full max-w-[1440px] flex-1 px-(--space-gutter) py-5 focus:outline-none md:py-6 xl:py-8">
          <Suspense fallback={<SkeletonBlock lines={8} />}>
            <Outlet />
          </Suspense>
        </main>
      </div>
    </div>
  );
}

function matchesPath(pattern: string, pathname: string) {
  const re = new RegExp(`^/${pattern.replace(/:[^/]+/g, '[^/]+')}/?$`);
  return re.test(pathname);
}
